import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, Camera, Upload, IdCard, BookOpen, FileCheck2,
    ChevronRight, ChevronLeft, CheckCircle2, XCircle, AlertTriangle,
} from "lucide-react";
import { publicApi } from "@/lib/publicApi";
import OnboardingHero from "@/components/OnboardingHero";

const ID_TYPES = [
    { value: "drivers_license", label: "Driver's License", icon: IdCard },
    { value: "passport", label: "Passport", icon: BookOpen },
    { value: "state_id", label: "State ID", icon: FileCheck2 },
];

const STEPS = ["welcome", "id_upload", "selfie", "review", "success"];

const MAX_BYTES = 10 * 1024 * 1024;
const ID_MIME_OK = new Set(["image/jpeg", "image/png", "image/webp", "application/pdf"]);
const SELFIE_MIME_OK = new Set(["image/jpeg", "image/png", "image/webp"]);

export default function VerifyIdPage() {
    const { token } = useParams();
    const [meta, setMeta] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [step, setStep] = useState("welcome");
    const [idType, setIdType] = useState("drivers_license");
    const [idFile, setIdFile] = useState(null);      // { file, previewUrl, mime, size }
    const [selfieFile, setSelfieFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        publicApi.get(`/id-verify/${token}`)
            .then((r) => setMeta(r.data))
            .catch((err) => {
                const code = err?.response?.status;
                const detail = err?.response?.data?.detail || "Couldn't load link.";
                setLoadError({ code, detail });
            });
    }, [token]);

    // Auto-revoke preview URLs
    useEffect(() => () => {
        [idFile, selfieFile].forEach((f) => f?.previewUrl && URL.revokeObjectURL(f.previewUrl));
    }, [idFile, selfieFile]);

    const submitAll = async () => {
        if (!idFile || !selfieFile) return;
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("id_type", idType);
            fd.append("id_document", idFile.file);
            fd.append("selfie", selfieFile.file);
            await publicApi.post(`/id-verify/${token}/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await publicApi.post(`/id-verify/${token}/submit`);
            setStep("success");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (loadError) return <ErrorScreen code={loadError.code} detail={loadError.detail} />;
    if (!meta) return <LoadingScreen />;

    return (
        <div
            className="min-h-screen w-full"
            style={{ background: "linear-gradient(135deg,#061935 0%,#0A2540 55%,#1E6FD9 100%)" }}
            data-testid="verify-id-page"
        >
            <div className="mx-auto flex min-h-screen max-w-xl flex-col px-5 pb-10 sm:px-6 sm:pb-12">
                <OnboardingHero variant="dark" eyebrow="Identity Verification" />
                <StepProgress step={step} />
                <main className="mt-6 flex-1">
                    {step === "welcome" && (
                        <WelcomeStep meta={meta} onContinue={() => setStep("id_upload")} />
                    )}
                    {step === "id_upload" && (
                        <IdUploadStep
                            idType={idType}
                            setIdType={setIdType}
                            idFile={idFile}
                            setIdFile={setIdFile}
                            onBack={() => setStep("welcome")}
                            onNext={() => setStep("selfie")}
                        />
                    )}
                    {step === "selfie" && (
                        <SelfieStep
                            selfieFile={selfieFile}
                            setSelfieFile={setSelfieFile}
                            onBack={() => setStep("id_upload")}
                            onNext={() => setStep("review")}
                        />
                    )}
                    {step === "review" && (
                        <ReviewStep
                            idType={idType}
                            idFile={idFile}
                            selfieFile={selfieFile}
                            submitting={submitting}
                            onBack={() => setStep("selfie")}
                            onSubmit={submitAll}
                        />
                    )}
                    {step === "success" && <SuccessStep />}
                </main>
                <footer className="mt-10 text-center text-[11px] italic text-white/60" data-testid="verify-id-footer">
                    {meta.compliance_footer || "Moving Mountains will never ask for payment information during onboarding."}
                </footer>
            </div>
        </div>
    );
}

/* ----------------------- sub-components ------------------------------ */

function StepProgress({ step }) {
    const idx = STEPS.indexOf(step);
    const pct = idx === 0 ? 10 : idx === STEPS.length - 1 ? 100 : ((idx) / (STEPS.length - 1)) * 100;
    return (
        <div className="mt-5 h-1 w-full overflow-hidden rounded-full bg-white/15">
            <div
                className="h-full rounded-full bg-gradient-to-r from-[#4BA3F5] to-[#C9A84C] transition-[width] duration-500"
                style={{ width: `${pct}%` }}
                data-testid="verify-id-progress"
            />
        </div>
    );
}

function WelcomeStep({ meta, onContinue }) {
    return (
        <section className="rounded-2xl bg-white p-7 shadow-xl" data-testid="verify-step-welcome">
            <div className="mm-eyebrow" style={{ color: "#1E6FD9" }}>Step 1 · Welcome</div>
            <h1 className="mm-display mt-1 text-3xl text-mm-navy sm:text-4xl">
                Hi {meta.first_name}, let&rsquo;s verify your identity.
            </h1>
            <p className="mt-3 text-sm text-mm-muted">
                This takes about <strong className="text-mm-navy">2 minutes</strong>. You&rsquo;ll need:
            </p>
            <ul className="mt-4 space-y-2.5 text-sm text-mm-ink">
                <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: "#0A6E43" }} />
                    A clear photo of your driver&rsquo;s license, passport, or state ID
                </li>
                <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: "#0A6E43" }} />
                    A well-lit selfie
                </li>
                <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: "#0A6E43" }} />
                    A quick confirmation
                </li>
            </ul>
            <p className="mt-5 rounded-md bg-[#F4F8FC] px-3 py-2.5 text-[11px] text-mm-muted">
                By submitting, you agree to identity verification by Moving Mountains for compliance purposes.
                Your documents are stored securely and never sold.
            </p>
            <button
                onClick={onContinue}
                className="mm-btn-primary mt-6 w-full justify-center"
                data-testid="verify-continue-btn"
            >
                Get started <ChevronRight size={16} />
            </button>
        </section>
    );
}

function IdUploadStep({ idType, setIdType, idFile, setIdFile, onBack, onNext }) {
    return (
        <section className="rounded-2xl bg-white p-6 shadow-xl sm:p-7" data-testid="verify-step-id">
            <div className="mm-eyebrow" style={{ color: "#1E6FD9" }}>Step 2 · Your ID</div>
            <h2 className="mm-display mt-1 text-2xl text-mm-navy">Choose your ID type</h2>
            <div className="mt-4 grid grid-cols-3 gap-2">
                {ID_TYPES.map((t) => {
                    const Icon = t.icon;
                    const active = idType === t.value;
                    return (
                        <button
                            key={t.value}
                            onClick={() => setIdType(t.value)}
                            className={`flex flex-col items-center gap-1.5 rounded-xl border-2 px-2 py-3 text-[11px] font-medium transition-all ${
                                active
                                    ? "border-[#1E6FD9] bg-[#EFF6FF] text-mm-navy"
                                    : "border-mm-line bg-white text-mm-ink hover:border-[#1E6FD9]/60"
                            }`}
                            data-testid={`verify-id-type-${t.value}`}
                        >
                            <Icon size={22} />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <h3 className="mm-display mt-6 text-lg text-mm-navy">Upload front of ID</h3>
            <FilePicker
                file={idFile}
                setFile={setIdFile}
                accept="image/jpeg,image/png,image/webp,application/pdf"
                capture="environment"
                allowedMimes={ID_MIME_OK}
                testid="verify-id-file"
                helperText="Tap to take a photo or choose from files. JPEG, PNG, WEBP, or PDF under 10MB."
            />

            <div className="mt-6 flex items-center justify-between gap-2">
                <button
                    onClick={onBack}
                    className="mm-btn-outline"
                    data-testid="verify-back-btn"
                >
                    <ChevronLeft size={16} /> Back
                </button>
                <button
                    onClick={onNext}
                    disabled={!idFile}
                    className="mm-btn-primary disabled:opacity-40"
                    data-testid="verify-next-btn"
                >
                    Next <ChevronRight size={16} />
                </button>
            </div>
        </section>
    );
}

function SelfieStep({ selfieFile, setSelfieFile, onBack, onNext }) {
    return (
        <section className="rounded-2xl bg-white p-6 shadow-xl sm:p-7" data-testid="verify-step-selfie">
            <div className="mm-eyebrow" style={{ color: "#1E6FD9" }}>Step 3 · Selfie</div>
            <h2 className="mm-display mt-1 text-2xl text-mm-navy">Take a quick selfie</h2>
            <p className="mt-2 text-sm text-mm-muted">
                Take a clear, well-lit photo of your face. Look straight at the camera.
            </p>
            <div className="mt-4">
                <FilePicker
                    file={selfieFile}
                    setFile={setSelfieFile}
                    accept="image/jpeg,image/png,image/webp"
                    capture="user"
                    allowedMimes={SELFIE_MIME_OK}
                    testid="verify-selfie-file"
                    helperText="Tap to open the camera or choose from your photos. JPEG, PNG, or WEBP under 10MB."
                />
            </div>
            <div className="mt-6 flex items-center justify-between gap-2">
                <button
                    onClick={onBack}
                    className="mm-btn-outline"
                    data-testid="verify-back-btn"
                >
                    <ChevronLeft size={16} /> Back
                </button>
                <button
                    onClick={onNext}
                    disabled={!selfieFile}
                    className="mm-btn-primary disabled:opacity-40"
                    data-testid="verify-next-btn"
                >
                    Next <ChevronRight size={16} />
                </button>
            </div>
        </section>
    );
}

function ReviewStep({ idType, idFile, selfieFile, submitting, onBack, onSubmit }) {
    const typeLabel = ID_TYPES.find((t) => t.value === idType)?.label || idType;
    return (
        <section className="rounded-2xl bg-white p-6 shadow-xl sm:p-7" data-testid="verify-step-review">
            <div className="mm-eyebrow" style={{ color: "#1E6FD9" }}>Step 4 · Review</div>
            <h2 className="mm-display mt-1 text-2xl text-mm-navy">Looks good?</h2>
            <p className="mt-2 text-sm text-mm-muted">
                One last check before you submit. Re-upload anything that isn&rsquo;t clear.
            </p>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-mm-line p-3">
                    <div className="text-[10px] uppercase tracking-wider text-mm-muted">{typeLabel}</div>
                    <PreviewThumb file={idFile} alt="ID" />
                </div>
                <div className="rounded-xl border border-mm-line p-3">
                    <div className="text-[10px] uppercase tracking-wider text-mm-muted">Selfie</div>
                    <PreviewThumb file={selfieFile} alt="Selfie" />
                </div>
            </div>

            <div className="mt-5 rounded-md bg-[#F4F8FC] px-3 py-2.5 text-[11px] text-mm-muted">
                By submitting, you agree to identity verification by Moving Mountains for compliance purposes.
                Your documents are stored securely and never sold.
            </div>

            <div className="mt-6 flex items-center justify-between gap-2">
                <button
                    onClick={onBack}
                    disabled={submitting}
                    className="mm-btn-outline disabled:opacity-50"
                    data-testid="verify-back-btn"
                >
                    <ChevronLeft size={16} /> Back
                </button>
                <button
                    onClick={onSubmit}
                    disabled={submitting || !idFile || !selfieFile}
                    className="mm-btn-primary disabled:opacity-50"
                    data-testid="verify-submit-btn"
                >
                    {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <>Submit <CheckCircle2 size={16} /></>}
                </button>
            </div>
        </section>
    );
}

function SuccessStep() {
    return (
        <section className="rounded-2xl bg-white p-8 text-center shadow-xl" data-testid="verify-step-success">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#0A6E43]/10 text-[#0A6E43]">
                <CheckCircle2 size={34} />
            </div>
            <h2 className="mm-display text-2xl text-mm-navy">Submitted.</h2>
            <p className="mt-2 text-sm text-mm-muted">
                We&rsquo;ll review and email you within 24 hours. You can close this page.
            </p>
        </section>
    );
}

function ErrorScreen({ code, detail }) {
    const Icon = code === 410 ? AlertTriangle : XCircle;
    const title = code === 410
        ? "This link has expired."
        : code === 404
            ? "Invalid link."
            : "Something went wrong.";
    return (
        <div
            className="flex min-h-screen items-center justify-center px-5"
            style={{ background: "linear-gradient(135deg,#061935 0%,#0A2540 55%,#1E6FD9 100%)" }}
            data-testid="verify-id-error"
        >
            <div className="w-full max-w-md rounded-2xl bg-white p-7 text-center shadow-xl">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                    <Icon size={32} />
                </div>
                <h2 className="mm-display text-2xl text-mm-navy">{title}</h2>
                <p className="mt-2 text-sm text-mm-muted">{detail}</p>
                <p className="mt-5 text-xs text-mm-muted">
                    Reach out to your Moving Mountains admin and ask for a fresh link.
                </p>
            </div>
        </div>
    );
}

function LoadingScreen() {
    return (
        <div className="flex min-h-screen items-center justify-center" style={{ background: "#0A2540" }}>
            <Loader2 className="animate-spin text-white" size={36} />
        </div>
    );
}

function FilePicker({ file, setFile, accept, capture, allowedMimes, testid, helperText }) {
    const inputRef = useRef(null);
    const onChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > MAX_BYTES) {
            toast.error("File is over 10MB. Please compress or retake.");
            e.target.value = "";
            return;
        }
        if (!allowedMimes.has(f.type)) {
            toast.error(`Unsupported type (${f.type || "unknown"}). Use JPEG, PNG, WEBP${accept.includes("pdf") ? ", or PDF" : ""}.`);
            e.target.value = "";
            return;
        }
        const previewUrl = f.type.startsWith("image/") ? URL.createObjectURL(f) : null;
        setFile({ file: f, previewUrl, mime: f.type, size: f.size, name: f.name });
    };
    return (
        <div>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                capture={capture}
                className="hidden"
                onChange={onChange}
                data-testid={`${testid}-input`}
            />
            {file ? (
                <div
                    className="flex items-center gap-3 rounded-xl border-2 border-mm-line bg-[#F4F8FC] p-3"
                    data-testid={`${testid}-filled`}
                >
                    {file.previewUrl ? (
                        <img
                            src={file.previewUrl}
                            alt="preview"
                            className="h-16 w-16 shrink-0 rounded-md object-cover"
                        />
                    ) : (
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-white text-mm-muted">
                            <FileCheck2 size={22} />
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-mm-navy">{file.name}</div>
                        <div className="text-xs text-mm-muted">{(file.size / 1024).toFixed(0)} KB</div>
                    </div>
                    <button
                        onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ""; }}
                        className="mm-link text-xs"
                        data-testid={`${testid}-replace`}
                    >
                        Replace
                    </button>
                </div>
            ) : (
                <button
                    onClick={() => inputRef.current?.click()}
                    className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#1E6FD9]/40 bg-[#F4F8FC] px-4 py-6 text-center transition-colors hover:border-[#1E6FD9]"
                    data-testid={`${testid}-empty`}
                >
                    {capture === "user" ? <Camera size={22} /> : <Upload size={22} />}
                    <div className="text-sm font-medium text-mm-navy">
                        {capture === "user" ? "Open camera" : "Tap to upload or take a photo"}
                    </div>
                    <div className="max-w-xs text-[11px] text-mm-muted">{helperText}</div>
                </button>
            )}
        </div>
    );
}

function PreviewThumb({ file, alt }) {
    if (!file) return <div className="mt-2 h-24 rounded-md bg-mm-bg" />;
    return file.previewUrl ? (
        <img src={file.previewUrl} alt={alt} className="mt-2 h-24 w-full rounded-md object-cover" />
    ) : (
        <div className="mt-2 flex h-24 items-center justify-center rounded-md bg-mm-bg text-mm-muted">
            <FileCheck2 size={28} />
        </div>
    );
}
